import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Event } from "../../types/model";
import TopRowCard from "../HomePage/components/TopRowCard";
import {
	fetchSeasonsThunk,
	fetchSeriesThunk,
	fetchSportsThunk,
	getEventsThunk,
} from "../../store/thunks/fantasyThunk";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	initializeEventContext,
	setSelectedEvent,
} from "../../store/slices/fantasySlice";
import {
	getFutureEvents,
} from "../../utils/helper";

const EventsBar = () => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const dispatch = useAppDispatch();
	const { selectedEvent, events, selectedSport } = useAppSelector(
		(state: RootState) => state.fantasy,
	);
	const { user } = useAppSelector((state: RootState) => state.user);
	const initializedForPreferenceRef = useRef<string>("");

	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const toId = (value: unknown): string => {
		if (value === undefined || value === null) {
			return "";
		}
		if (typeof value === "object") {
			const maybeDoc = value as { _id?: unknown; id?: unknown; $oid?: unknown };
			if (maybeDoc._id !== undefined && maybeDoc._id !== null) {
				return String(maybeDoc._id);
			}
			if (maybeDoc.id !== undefined && maybeDoc.id !== null) {
				return String(maybeDoc.id);
			}
			if (maybeDoc.$oid !== undefined && maybeDoc.$oid !== null) {
				return String(maybeDoc.$oid);
			}
		}
		return String(value);
	};

	const scroll = (scrollOffset: number) => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft += scrollOffset;
		}
	};

	const allFutureEvents = useMemo(() => getFutureEvents(events), [events]);

	const filteredFutureEvents = useMemo(
		() =>
			allFutureEvents.filter((event) => {
				return selectedSport?._id
					? toId(event.sport?._id) === toId(selectedSport?._id)
					: true;
			}),
		[allFutureEvents, selectedSport?._id],
	);

	const nearestFutureEvent = filteredFutureEvents[0] ?? allFutureEvents[0] ?? null;
	const preferenceKey = `${toId(user?._id) || "guest"}:${toId(user?.favoriteSportId)}`;

	useEffect(() => {
		dispatch(getEventsThunk());
		dispatch(fetchSportsThunk());
		dispatch(fetchSeriesThunk());
		dispatch(fetchSeasonsThunk());

		const handleResize = () => {
			updateScrollState();
		};

		handleResize(); // Initial check
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [dispatch]);

	const updateScrollState = () => {
		const container = scrollContainerRef.current;
		if (!container) return;
		const { scrollLeft, scrollWidth, clientWidth } = container;
		const hasOverflow = scrollWidth > clientWidth + 1;
		setCanScrollLeft(hasOverflow && scrollLeft > 0);
		setCanScrollRight(hasOverflow && scrollLeft + clientWidth < scrollWidth - 1);
	};

	useEffect(() => {
		updateScrollState();
	}, [filteredFutureEvents]);

	useEffect(() => {
		if (!events.length) {
			return;
		}

		if (initializedForPreferenceRef.current === preferenceKey) {
			return;
		}

		dispatch(
			initializeEventContext({
				preferredSportId: toId(user?.favoriteSportId) || undefined,
			}),
		);
		initializedForPreferenceRef.current = preferenceKey;
	}, [dispatch, events.length, preferenceKey, user?.favoriteSportId]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;
		const handleScroll = () => updateScrollState();
		container.addEventListener("scroll", handleScroll, { passive: true });
		return () => container.removeEventListener("scroll", handleScroll);
	}, []);

	// useEffect(() => {
	// 	if (!selectedEvent) {
	// 		dispatch(setSelectedEvent(nearestFutureEvent));
	// 	}
	// }, [events]);

	const handleSelectEvent = (event: Event) => {
		dispatch(setSelectedEvent(event));
	};

	const scrollIntervalRef = useRef<number | null>(null);

	const startAutoScroll = (direction: "left" | "right") => {
		if (scrollIntervalRef.current) return;
		scrollIntervalRef.current = window.setInterval(() => {
			scroll(direction === "left" ? -8 : 8);
		}, 16);
	};

	const stopAutoScroll = () => {
		if (scrollIntervalRef.current) {
			window.clearInterval(scrollIntervalRef.current);
			scrollIntervalRef.current = null;
		}
	};

	useEffect(() => {
		return () => stopAutoScroll();
	}, []);

	return (
		<div
			className="events-bar bg-secondary position-relative"
			style={{
				display: "flex",
				alignItems: "center",
				height: "6rem",
				overflow: "hidden",
				gap: "0.12rem",
				padding: "0 0.5rem 0 0",
			}}
		>
			<div
				className={`events-bar-gradient events-bar-gradient-left ${
					canScrollLeft ? "events-bar-gradient--visible" : ""
				}`}
			/>
			<Button
				variant="secondary"
				onClick={() => scroll(-400)}
				className="events-bar-scroll-btn"
				aria-label="Scroll events left"
				onMouseEnter={() => startAutoScroll("left")}
				onMouseLeave={stopAutoScroll}
			>
				&lt;
			</Button>
			<div
				ref={scrollContainerRef}
				style={{
					overflowX: "auto",
					whiteSpace: "nowrap",
					flex: 1,
				}}
				className="bg-secondary"
			>
				{filteredFutureEvents.map((event) => (
					<React.Fragment key={event?._id}>
						<div
							style={{
								display: "inline-block",
								width: "16rem",
								margin: "0.5rem",
								verticalAlign: "middle",
							}}
						>
							<TopRowCard
								event={event}
								nearestFutureEvent={nearestFutureEvent}
								selectedEvent={selectedEvent}
								handleSelectEvent={handleSelectEvent}
							/>
						</div>
						{
							<div
								style={{
									height: "150px",
									width: "2px",
									display: "inline-block",
									verticalAlign: "middle",
								}}
								className="bg-dark"
							/>
						}
					</React.Fragment>
					))}
				</div>

			<Button
				variant="secondary"
				onClick={() => scroll(400)}
				className="events-bar-scroll-btn"
				aria-label="Scroll events right"
				onMouseEnter={() => startAutoScroll("right")}
				onMouseLeave={stopAutoScroll}
			>
				&gt;
			</Button>
			<div
				className={`events-bar-gradient events-bar-gradient-right ${
					canScrollRight ? "events-bar-gradient--visible" : ""
				}`}
			/>
		</div>
	);
};

export default EventsBar;
